import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, getStoredUser } from "../api";
import { MicIcon, TrendingUpIcon, PhoneIcon, InsightsIcon, CoachingIcon, PlaylistIcon } from "../components/Icons.jsx";

/* CallIQ — the calls area of RepIQ. One home for everything call-intelligence:
   Recordings (the library), Missed & voicemail, Insights, Coaching and Playlists, as tabs.
   Deep links still work: each tab keeps its original route.

   Salem, 2026-09-08: *"I want the Missed and Voice messages to be inside CallIQ (with the
   notification in CallIQ) between Recordings and Insights."* Right place for it — a missed
   call is a call, and it belongs beside the ones that were answered rather than in its own
   corner of the sidebar. The count comes with it, so the badge lives on CallIQ. */

/* Design system 1.0 (5.2): the tab strip is a segmented control in the RepIQ themes, and UI
   chrome carries no emoji — each tab leads with a 16px outline icon in a 20px box instead. */
const TABS = [
  ["recordings", "Recordings", "/recordings", MicIcon],
  // Trackers moved out of its own sidebar entry and into CallIQ as a tab, between Recordings
  // and Missed & voicemail (Salem, 2026-09-25) — it is manager/admin only, same gate as before
  // (ManagerArea on the /trackers route), so it is filtered out of TABS below for anyone else
  // rather than shown and then bounced.
  ["trackers", "Trackers", "/trackers", TrendingUpIcon],
  ["missed", "Missed & voicemail", "/missed", PhoneIcon],
  ["insights", "Insights", "/insights", InsightsIcon],
  ["coaching", "Coaching", "/insights/coaching", CoachingIcon],
  ["playlists", "Playlists", "/playlists", PlaylistIcon],
];

function activeTab(pathname) {
  if (pathname.startsWith("/playlists")) return "playlists";
  if (pathname.startsWith("/trackers")) return "trackers";
  if (pathname.startsWith("/missed")) return "missed";
  if (pathname.startsWith("/insights/coaching")) return "coaching";
  if (pathname.startsWith("/insights")) return "insights";
  return "recordings";
}

export default function CallIQHub({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = activeTab(pathname);
  const user = getStoredUser();
  const canSeeTrackers = user?.role === "admin" || user?.sales_role === "manager";
  const tabs = TABS.filter(([k]) => k !== "trackers" || canSeeTrackers);

  /* The count of open missed calls and voicemails this person can see and act on — their own
     line for a rep, the local business for a manager or Operations (Salem 2026-09-08). Above
     the importance floor only, so it is not the raw number of ring-offs.

     Same polling contract as the sidebar's other badges — quiet while the tab is hidden,
     re-checked on every navigation, so it goes DOWN the moment you have dealt with
     something. A badge that only ever climbs teaches people to ignore it. */
  const [waiting, setWaiting] = useState(0);
  const load = useCallback(() => {
    if (document.visibilityState === "hidden") return;
    api.get("/api/missed/count")
      .then((d) => setWaiting(d.count || 0))
      .catch(() => {});          // a badge is never worth an error on screen
  }, []);
  useEffect(() => {
    const t = setInterval(load, 60_000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("repiq:missed-read", load);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("repiq:missed-read", load);
    };
  }, [load]);
  useEffect(() => { load(); }, [load, pathname]);

  return (
    <div className="page" style={{ maxWidth: 1600 }}>
      <div className="page-head">
        <div className="page-head-text">
          <p className="eyebrow">Conversations</p>
          <h1 className="page-title">CallIQ</h1>
          <p className="page-sub">Recordings, missed calls, insights, coaching and playlists — every call in one place.</p>
        </div>
      </div>
      <div className="tabs rq-tabs ciq-hub-tabs">
        {tabs.map(([k, label, path, Icon]) => (
          <button key={k} onClick={() => navigate(path)}
            className={"tab" + (active === k ? " active" : "")}>
            <span className="ico-20" aria-hidden="true"><Icon size={16} /></span>
            {label}
            {k === "missed" && waiting > 0 && (
              <span className="count ciq-hub-count">{waiting}</span>
            )}
          </button>
        ))}
      </div>
      {children}
    </div>
  );
}
